import React, { useState, useMemo, useEffect } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
  ExpandedState,
  Row,
  HeaderContext,
} from '@tanstack/react-table';
import { ChevronUp } from 'lucide-react';

import { cn, formatNumberWithCommas, getProfitLossClass } from '@/utils';
import Text from '@/utils/responsiveText';
import { DataTableSkeleton } from '../ui/DataTableskeleton';

type Align = 'left' | 'center' | 'right';

type MyMeta = {
  align?: Align;
};

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  // ... other props unchanged
  onSelectionChange?: (selectedRows: TData[]) => void;
  enableSelection?: boolean;
  enableAccordion?: boolean;
  pageSize?: number;
  variant?: 'default' | 'primary' | 'secondary';
  isLoading?: boolean;
  renderSubRow?: (rowData: TData) => React.ReactNode;
  accordionPosition?: number;
  headerClassName?: string;
  rowvariant?: 'default' | 'green';
  total?: boolean;
  totals?: Record<string, any>;
  rows?: number;
  rowClassNames?: string;
}

export function DataTable<TData>({
  data,
  columns,
  onSelectionChange,
  enableSelection = false,
  headerClassName,
  enableAccordion = false,
  pageSize = 10,
  variant = 'default',
  rowvariant = 'default',
  total,
  totals,
  isLoading,
  renderSubRow,
  accordionPosition,
  rows = 10,
  rowClassNames,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});

  useEffect(() => {
    setExpanded({});
  }, [data]);

  const tableColumns = useMemo(() => {
    const baseColumns = enableSelection
      ? [
          {
            id: 'select',
            header: ({ table }: HeaderContext<TData, unknown>) => (
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={table.getIsAllPageRowsSelected()}
                onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
              />
            ),
            cell: ({ row }: { row: Row<TData> }) => (
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={row.getIsSelected()}
                onChange={(e) => row.toggleSelected(e.target.checked)}
              />
            ),
            enableSorting: false,
            enableHiding: false,
          },
        ]
      : [];

    if (!enableAccordion) return [...baseColumns, ...columns];

    const expandColumn: ColumnDef<TData> = {
      id: 'expander',
      header: () => null,
      cell: ({ row }: { row: Row<TData> }) =>
        row.getCanExpand() ? (
          <button
            {...{
              onClick: row.getToggleExpandedHandler(),
              style: { cursor: 'pointer' },
              'aria-label': row.getIsExpanded() ? 'Collapse' : 'Expand',
              className: 'p-1',
            }}
          >
            <div className="bg-green3 rounded-full ">
              <ChevronUp
                size={20}
                className={`${row.getIsExpanded() ? '' : 'rotate-180'} transition-all duration-300`}
              />
            </div>
          </button>
        ) : null,
      enableSorting: false,
      enableHiding: false,
    };

    const fullCols = [...baseColumns, ...columns];
    const pos = accordionPosition ?? baseColumns.length;
    fullCols.splice(pos, 0, expandColumn);

    return fullCols;
  }, [columns, enableSelection, enableAccordion, accordionPosition]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
      expanded,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
    getRowCanExpand: () => Boolean(enableAccordion),
  });

  React.useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, onSelectionChange, table]);

  const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);

  const headerVariant = {
    default: 'bg-gray-200 text-gray-700',
    primary: 'bg-blue-600 text-white',
    secondary: ' text-textcolor',
  };

  const rowVariant = {
    default: 'bg-muted',
    green: 'bg-green/5',
  };

  const visibleColumnCount = table.getVisibleFlatColumns().length;

  return (
    <div className="space-y-2 relative">
      <div className="border border-green3 rounded-t-xl max-h-[510px] overflow-x-auto">
        <table className="min-w-max w-full table-auto">
          <thead className="border-b border-green3 rounded-xl bg-tableHead ">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const align = (header.column.columnDef.meta as MyMeta | undefined)?.align;
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'h-12 px-4 font-medium text-[13px] text-left',
                        align === 'center' && 'text-center',
                        align === 'right' && 'text-right',
                        headerVariant[variant],
                        headerClassName,
                        header.index === 0 && 'rounded-tl-xl',
                        header.index === headerGroup.headers.length - 1 && 'rounded-tr-xl'
                      )}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            'inline-flex items-center text-light gap-2 w-full',
                            align === 'center' && 'justify-center',
                            align === 'right' && 'justify-end',
                            header.column.getCanSort() && 'cursor-pointer select-none'
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading ? (
              <DataTableSkeleton columns={table.getVisibleLeafColumns().length} rows={rows} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={`main_${row.id}`}>
                  <tr
                    key={`row_${row.id}`}
                    className={cn(
                      `border-b border-green3 transition-colors text-left ${rowClassNames}`,
                      rowVariant[rowvariant],
                      row.getIsSelected() && 'bg-muted'
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={`cell_${cell.id}`}
                        className="px-4 py-1.5 align-middle text-xs text-black"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>

                  {row.getIsExpanded() && (
                    <tr key={`sub_${row.id}`}>
                      <td colSpan={visibleColumnCount} className="border-b border-green3 p-0">
                        <div className="w-full overflow-x-auto">
                          <div style={{ width: '100%' }}>
                            <div
                              style={{
                                width: '100%',
                              }}
                              className="p-1"
                            >
                              {renderSubRow ? renderSubRow(row.original) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={visibleColumnCount} className="h-24 text-center">
                  <img src="/images/empty.png" className="mx-auto h-44" />
                  <Text min={22} max={30} as="h3" className="font-semibold text-gray">
                    No data available.
                  </Text>
                </td>
              </tr>
            )}
          </tbody>

          {total && totals && table.getRowModel().rows?.length !== 0 && (
            <tfoot>
              <tr className="bg-gray text-[13px] border-t border-green">
                {table.getVisibleLeafColumns().map((column, index) => {
                  const key = column.id;
                  const value = totals ? totals[key] : undefined;

                  return (
                    <td
                      key={key}
                      className={`px-4 py-2 text-white  text-center ${getProfitLossClass(value)}`}
                    >
                      {index === 0 ? (
                        <span className="">Total</span>
                      ) : value !== undefined ? (
                        formatNumberWithCommas(value)
                      ) : (
                        ''
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
export default DataTable;
